/**
 * Shared template variable utilities used by both the WYSIWYG (ngx-editor)
 * and SMS (contenteditable) template editors.
 */

export interface TemplateVariable {
    label: string;
    value: string;
    description?: string;
}

/**
 * Builds a global regex that matches any known variable token.
 * Returns null if no variables are provided.
 */
export function buildVariablePattern(variables: TemplateVariable[]): RegExp | null {
    if (variables.length === 0) return null;
    const escapedTokens = variables.map(v =>
        v.value.replace(/([{}#])/g, '\\$1')
    );
    return new RegExp(`(${escapedTokens.join('|')})`, 'g');
}

/**
 * Escapes HTML special characters in a plain-text string.
 */
export function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/**
 * Decodes the HTML entities produced by escapeHtml (plus &nbsp;) back to plain
 * text. &amp; is decoded last so an encoded entity like &amp;lt; round-trips
 * correctly. Used to turn editor HTML back into plain text without touching the
 * DOM (avoids an innerHTML XSS surface).
 */
export function unescapeHtml(html: string): string {
    return html
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, '\'')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&');
}

/**
 * Generates a key string representing variable match positions in the text.
 * Used to avoid unnecessary re-renders when text changes don't affect variables.
 */
export function getVariableMatchKey(text: string, variables: TemplateVariable[]): string {
    const pattern = buildVariablePattern(variables);
    if (!pattern) return '';

    const matches: string[] = [];
    let match: RegExpExecArray | null;
    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
        matches.push(`${match.index}:${match[0]}`);
    }
    return matches.join(',');
}

/**
 * Builds an HTML string with variable tokens wrapped in styled,
 * non-editable spans. Used by the SMS editor for rendering and
 * available for any contenteditable-based editor.
 */
export function buildStyledHtml(text: string, variables: TemplateVariable[]): string {
    if (text.length === 0) return '';

    const pattern = buildVariablePattern(variables);
    if (!pattern) return escapeHtml(text);

    const variableMap = new Map<string, TemplateVariable>();
    variables.forEach(v => variableMap.set(v.value, v));

    let lastIndex = 0;
    const fragments: string[] = [];
    let match: RegExpExecArray | null;

    pattern.lastIndex = 0;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            fragments.push(escapeHtml(text.substring(lastIndex, match.index)));
        }
        const variable = variableMap.get(match[0]);
        const tooltipText = variable?.description || match[0];
        fragments.push(
            `<span class="template-variable-tag" contenteditable="false" title="${escapeHtml(tooltipText)}">${escapeHtml(match[0])}</span>`
        );
        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        fragments.push(escapeHtml(text.substring(lastIndex)));
    }

    return fragments.join('');
}

/**
 * Strips template variable bridge spans back to plain text tokens.
 * Converts `<span ... data-template-variable="{TOKEN}" ...>any text</span>` → `{TOKEN}`.
 * Used by the WYSIWYG editor to produce backend-compatible HTML.
 */
export function stripVariableSpans(html: string): string {
    if (!html) return html;
    return html.replace(
        /<span[^>]*\sdata-template-variable="([^"]*)"[^>]*>[^<]*<\/span>/g,
        '$1'
    );
}
